const PASSPORT_VALIDITY_MONTHS = 6;

function parseDateValue(value) {
  const text = String(value || "").trim();
  if (!text) return null;
  const dateOnly = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  const parsed = dateOnly
    ? new Date(`${dateOnly[1]}-${dateOnly[2]}-${dateOnly[3]}T12:00:00`)
    : new Date(text);
  if (Number.isNaN(parsed.getTime())) return null;
  parsed.setHours(0, 0, 0, 0);
  return parsed;
}

function addMonths(date, months) {
  return new Date(date.getFullYear(), date.getMonth() + months, date.getDate());
}

function toDateKey(date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function validatePassenger(passenger = {}, { referenceDate = new Date() } = {}) {
  const firstName = String(passenger.uppd_fname || "").trim();
  const lastName = String(passenger.uppd_lname || "").trim();
  const passportNo = String(passenger.uppd_passport_no || "").trim();
  const expiry = parseDateValue(passenger.uppd_passport_expiry);

  if (!firstName) return "PASSENGER_FIRST_NAME";
  if (!lastName) return "PASSENGER_LAST_NAME";
  if (!passportNo) return "PASSENGER_PASSPORT";
  if (!expiry) return "PASSENGER_PASSPORT_EXPIRY";

  const minimumExpiry = addMonths(new Date(referenceDate), PASSPORT_VALIDITY_MONTHS);
  if (toDateKey(expiry) < toDateKey(minimumExpiry)) return "PASSENGER_PASSPORT_VALIDITY";
  return null;
}

export const PASSENGER_CSV_HEADERS = [
  "title",
  "first_name",
  "last_name",
  "gender",
  "date_of_birth",
  "passport_no",
  "passport_issue_date",
  "passport_expiry",
  "place_of_issue",
  "passenger_type",
];

export const PASSENGER_CSV_TEMPLATE = `${PASSENGER_CSV_HEADERS.join(",")}
Mr,Ali,Khan,Male,1990-01-15,A1234567,2018-06-01,2028-06-01,Mumbai,adult
`;

const HEADER_ALIASES = {
  title: "uppd_title",
  uppd_title: "uppd_title",
  first_name: "uppd_fname",
  firstname: "uppd_fname",
  fname: "uppd_fname",
  uppd_fname: "uppd_fname",
  last_name: "uppd_lname",
  lastname: "uppd_lname",
  lname: "uppd_lname",
  uppd_lname: "uppd_lname",
  gender: "uppd_gender",
  uppd_gender: "uppd_gender",
  date_of_birth: "uppd_dob",
  dob: "uppd_dob",
  uppd_dob: "uppd_dob",
  passport_no: "uppd_passport_no",
  passport: "uppd_passport_no",
  passport_number: "uppd_passport_no",
  uppd_passport_no: "uppd_passport_no",
  passport_issue_date: "uppd_passport_issue_date",
  uppd_passport_issue_date: "uppd_passport_issue_date",
  passport_expiry: "uppd_passport_expiry",
  passport_expiry_date: "uppd_passport_expiry",
  uppd_passport_expiry: "uppd_passport_expiry",
  place_of_issue: "uppd_passport_issue_place",
  passport_issue_place: "uppd_passport_issue_place",
  uppd_passport_issue_place: "uppd_passport_issue_place",
  passenger_type: "uppd_adult_type",
  adult_type: "uppd_adult_type",
  uppd_adult_type: "uppd_adult_type",
};

function normalizeHeader(value) {
  return String(value || "")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");
}

function splitCsvLine(line) {
  const cells = [];
  let current = "";
  let inQuotes = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (char === "," && !inQuotes) {
      cells.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
}

export function parsePassengerCsv(text) {
  const lines = String(text || "")
    .replace(/^\uFEFF/, "")
    .split(/\r?\n/)
    .map((line) => line.trimEnd())
    .filter((line) => line.trim());

  if (lines.length === 0) {
    return { error: "PASSENGER_CSV_EMPTY", rows: [] };
  }

  const headers = splitCsvLine(lines[0]).map(normalizeHeader);
  const mappedHeaders = headers.map((header) => HEADER_ALIASES[header] || null);
  if (!mappedHeaders.includes("uppd_fname") || !mappedHeaders.includes("uppd_passport_no")) {
    return { error: "PASSENGER_CSV_HEADERS", rows: [] };
  }

  const rows = lines.slice(1).map((line, index) => {
    const cells = splitCsvLine(line);
    const passenger = {
      uppd_title: "Mr",
      uppd_fname: "",
      uppd_lname: "",
      uppd_gender: "Male",
      uppd_dob: "",
      uppd_passport_no: "",
      uppd_passport_issue_date: "",
      uppd_passport_expiry: "",
      uppd_passport_issue_place: "",
      uppd_adult_type: "adult",
    };
    mappedHeaders.forEach((field, fieldIndex) => {
      if (!field) return;
      passenger[field] = cells[fieldIndex] || "";
    });
    return {
      line: index + 2,
      passenger,
      error: validatePassenger(passenger),
    };
  });

  return { error: null, rows };
}

export function downloadPassengerCsvTemplate() {
  const blob = new Blob([PASSENGER_CSV_TEMPLATE], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = "passenger-template.csv";
  anchor.click();
  window.URL.revokeObjectURL(url);
}
